import { useState } from 'react';
import dayjs from 'dayjs';
import { message } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import BookSlotModal from '@/components/shared/book-a-call/BookSlotModal';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { openModal } from '@/slice/modal';
import { requestReschedule } from '@/api/booking';

import { notifyActionError } from '@/helpers/subscriptionError';
interface RescheduleModalProps {
  open: boolean;
  onCancel: () => void;
  bookingId: string;
  consultantId: string;
  fee: string;
}

// Wraps the shared BookSlotModal calendar/slot picker for a franchisor's
// <24h reschedule request. Mirrors the availability/timezone/date state
// book-a-call/index.tsx sets up for the original booking flow, but submits
// via requestReschedule instead of bookACall, and "Back" returns to the
// late-cancel policy modal instead of just closing.
export const RescheduleModal = (props: RescheduleModalProps) => {
  const { bookingId, consultantId, fee } = props;
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [timezone, setTimezone] = useState(dayjs.tz?.guess?.() ?? 'UTC');
  const [availability, setAvailability] = useState<{ days: any[] }>({ days: [] });

  const submit = useMutation({
    mutationKey: ['request-reschedule', bookingId],
    mutationFn: (slot: { start: string; end: string }) =>
      requestReschedule(bookingId, { start: slot.start, end: slot.end, timeZone: timezone }),
    onSuccess: () => {
      message.success('Reschedule request sent.');
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['booking'] });
      props.onCancel();
    },
    // Surface the backend's actual reason (e.g. "This meeting has already
    // been rescheduled once...") instead of a generic failure — the
    // franchisor needs to know WHY, not just that it failed.
    // TCX-900: a Stripe outage 503s this write; say "retry", not "rejected",
    // and never surface the backend prose as UI copy.
    onError: (error: any) =>
      notifyActionError(error, 'Failed to send reschedule request.'),
  });

  const goBack = () =>
    dispatch(
      openModal({
        modalType: 'LATE_CANCEL_POLICY',
        modalProps: { bookingId, consultantId, fee },
      }),
    );

  return (
    <BookSlotModal
      consultantId={consultantId}
      visible={props.open}
      availability={availability}
      selectedDate={selectedDate}
      setSelectedDate={setSelectedDate}
      loading={false}
      onClose={goBack}
      onSlotSelect={(slot) => submit.mutate(slot)}
      isBooking={submit.isPending}
      timezone={timezone}
      onTimezoneChange={setTimezone}
      setAvailability={setAvailability}
      primaryLabel={"SEND RESCHEDULE REQUEST"}
      secondaryLabel={"BACK"}
      footerNote={
        <>
          <b>Note:</b> if approved, the {fee} fee is assigned to this new date. The consultant may
          still charge for both meetings.
        </>
      }
    />
  );
};
